import React, { ReactElement } from "react"
import { Link } from "gatsby"
import Layout from "../../components/layout"
import CSharp from "../../components/csharp"
import InlineCode from "../../components/inlineCode"
import Shell from "../../components/shell"

function indent(text: string, count: number) {
  return text.replace(/^(?!\s*$)/gm, " ".repeat(count))
}

const modelCode = `public class Employee
{
    public string FirstName { get; set; }
    public string LastName { get; set; }
    public string Email { get; set; }
    public string Phone { get; set; }
    public string[] Skills { get; set; }
    public decimal Salary { get; set; }
    public DateTime Commenced { get; set; }
    public bool Active { get; set; }
}`

const builderCode = `var validator = new ValidatorBuilder<Employee>()
    .Required(e => e.FirstName)
    .For(e => e.LastName, name => 
    {
        name.Required()
            .MaxLength(30);
    })
    .Email(e => e.Email)
    .Contains(e => e.Skills, "Cromulent")
    .Range(e => e.Salary, 50000, 100000)
    .LessThanOrEqual(e => e.Commenced, DateTime.Today)
    .Continue(v =>
    {
        v.True(e => e.Active);
    })
    .Build();`

const validationCode = `var employee = new Employee
{
    FirstName = "Homer",
    Email = "homer[at]springfieldnuclear.com",
    Salary = 45000,
    Skills = new[] { "Embiggening" }
};

var result = validator.Validate(employee);`

const resultCode = `if (!result.IsValid)
{
    foreach (var rule in result.BrokenRules)
    {
        Console.WriteLine($"[{rule.Rule}] {rule.Key}: {rule.Message}");
    }
};`

const programCode = `using System;
using Validatum;

namespace ConsoleApp
{
    public class Program
    {
        public static void Main(string[] args)
        {
${indent(builderCode, 12)}

${indent(validationCode, 12)}

${indent(resultCode, 12)}
        }

${indent(modelCode, 8)}
    }
}`

const outputCode = `[Required] LastName: Value is required.
[Email] Email: Value must be a valid email.
[Contains] Skills: Must contain item 'Cromulent'.
[Range] Salary: Value must be in range '50000' to '100000'.`

function FirstValidator(): ReactElement {
  return (
    <Layout
      title="Create your first Validator"
      description="Create your first validator using Validatum"
    >
      <h1>Create your first Validator</h1>
      <p>
        Building a validation function is accomplished with the{" "}
        <InlineCode code="ValidatorBuilder<T>" /> class. Where{" "}
        <InlineCode>T</InlineCode> is the type you want to validate.
      </p>
      <p>
        See{" "}
        <Link to="/building/validator-builder/">
          The ValidatorBuilder Class
        </Link>
        .
      </p>
      <hr className="my-6" />
      <p>
        For your first validator we will use the following{" "}
        <InlineCode>Employee</InlineCode> model.
      </p>
      <div className="mb-4">
        <CSharp code={modelCode} />
      </div>
      <p>Our validation function must satisfy the following rules:</p>
      <ul className="list mb-4">
        <li>
          <InlineCode>FirstName</InlineCode> is required.
        </li>
        <li>
          <InlineCode>LastName</InlineCode> is required and have no more than 30
          characters.
        </li>
        <li>
          <InlineCode>Email</InlineCode> must be a valid email.
        </li>
        <li>
          <InlineCode>Skills</InlineCode> must contain{" "}
          <strong>Cromulent</strong>.
        </li>
        <li>
          <InlineCode>Salary</InlineCode> between <strong>50,000</strong> and{" "}
          <strong>100,000</strong>.
        </li>
        <li>
          <InlineCode>Commenced</InlineCode> cannot be in the future.
        </li>
        <li>
          <InlineCode>Active</InlineCode> must be true only if previous rules
          pass.
        </li>
      </ul>
      <p>
        We can build a validator that will enforce the above rules like this:
      </p>
      <div className="mb-4">
        <CSharp code={builderCode} />
        <p>
          The <InlineCode>Build()</InlineCode> method will return a
          <InlineCode code="Validator<Employee>" /> which can be used to
          validate <InlineCode>Employee</InlineCode> instances.
        </p>
      </div>
      <div className="mb-4">
        <CSharp code={validationCode} />
        <p>
          The <InlineCode>Validate()</InlineCode> method returns a{" "}
          <InlineCode>ValidationResult</InlineCode> instance containing a
          collection of broken validation rules. See{" "}
          <Link to="/validation/results/">Validation Results</Link>.
        </p>
      </div>
      <div className="mb-4">
        <CSharp code={resultCode} />
      </div>
      <p>Putting it all together into an executable console application.</p>
      <div className="mb-4">
        <CSharp code={programCode} />
      </div>
      <p>Will produce the following output:</p>
      <div className="mb-4">
        <Shell code={outputCode} />
      </div>
    </Layout>
  )
}

export default FirstValidator
